/*
 * @Author: jianwei.chen
 * @Date: 2024-03-25 15:42:19
 * @Description: AmountWithSlider Store
 */

import { makeAutoObservable } from 'mobx';
import { calc, debounce, toLocale } from '@ok/blade';
import { instStore } from '@ok/trade-common';

import {
  DEFAULT_DEBOUNCE_DELAY,
  DEFAULT_DIG_4,
} from '_common/consts/bot/strategy';
import { INST_TYPE } from '_common/consts/trade';
import { getInstIdInfo } from '_common/utils/inst';

const { SPOT } = INST_TYPE;

class Store {
  constructor(props) {
    makeAutoObservable(this);
    const { instId, instType, onValueChange, available, min, max } = props;
    this.instId = instId;
    this.instType = instType;
    this.onValueChange = onValueChange;
    this.available = available || '';
    this.min = min || '';
    this.max = max || '';
  }

  min = '';
  max = '';
  available = '';
  amt = '';
  amtError = '';
  sliderValue = 0;

  get preText() {
    return this.instType === SPOT
      ? toLocale('trade_botpopup_title_spot_grid_investment_total')
      : toLocale('fdcabot_placeorder_item_current_usage');
  }

  get quoteCcy() {
    const { quoteCcy } = getInstIdInfo(this.instId);
    return quoteCcy;
  }

  get sizeDig() {
    const { sizeDig = DEFAULT_DIG_4 } = instStore.coins[this.quoteCcy] || {};
    return sizeDig;
  }

  get placeholder() {
    const minNum = calc.showCeilTruncation(this.min, this.sizeDig, {
      needZero: false,
    });
    const maxNum = calc.showFloorTruncation(this.max, this.sizeDig, {
      needZero: false,
    });
    if (this.min && this.max) {
      return `${minNum}~${maxNum}`;
    }
    if (this.min) {
      return `>${minNum}`;
    }
    if (this.max) {
      return `<${maxNum}`;
    }
    return '';
  }

  onChange = (value) => {
    this.clearDebuonce();
    this.amtError = '';
    this.amt = value;
    this.onValueChange(value);
    if (!value) {
      this.sliderValue = 0;
    }
    if (this.available && value) {
      const ratio = calc.div(value, this.available);
      this.sliderValue = calc.mul(ratio, 100);
    }
    this.validWithDebounce();
  };

  onSliderChange = (value) => {
    this.clearDebuonce();
    this.amtError = '';
    this.sliderValue = value;
    const ratio = calc.div(this.sliderValue, 100);
    this.amt = calc.mul(this.available, ratio);
    this.onValueChange(this.amt);
    this.validWithDebounce();
  };

  validWithDebounce = debounce(() => {
    this.valid();
  }, DEFAULT_DEBOUNCE_DELAY);

  clearDebuonce = () => {
    this.validWithDebounce?.cancel && this.validWithDebounce.cancel();
  };

  valid = () => {
    if (this.available && this.amt && +this.amt > +this.available) {
      this.amtError = toLocale('dcabot_placeorder_fieldalert_exceed_available');
      return false;
    }
    if (this.amt && this.min && +this.amt <= +this.min) {
      this.amtError = toLocale(
        'marketplace_placeorder_errortext_must_greater_than',
        {
          fieldName: this.preText,
          minValue: calc.showCeilTruncation(this.min, this.sizeDig, {
            needZero: false,
          }),
          unit: this.quoteCcy,
        }
      );
      return false;
    }
    if (this.amt && this.max && +this.amt >= +this.max) {
      this.amtError = toLocale(
        'marketplace_placeorder_errortext_must_less_than',
        {
          fieldName: this.preText,
          minValue: calc.showFloorTruncation(this.max, this.sizeDig, {
            needZero: false,
          }),
          unit: this.quoteCcy,
        }
      );
      return false;
    }
    return true;
  };

  validPass = () => {
    if (!this.amt) {
      this.amtError = toLocale('trade_bot_errortip_order_value_required', {
        text: this.preText,
      });
      return false;
    }
    const result = this.valid();
    return result;
  };

  resetData = () => {
    this.amtError = '';
    this.amt = '';
    this.sliderValue = 0;
  };

  update = (newData) => {
    Object.keys(newData).forEach((key) => {
      this[key] = newData[key];
    });
  };
}

export default Store;
